/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat, novacuum.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import LogInModal from 'flarum/forum/components/LogInModal';
import type { SavedModelData } from 'flarum/common/Model';
import type RequestError from 'flarum/common/utils/RequestError';
import type Mithril from 'mithril';
import AuthModal from '../components/AuthModal';
import type { LinkedAccountProvider } from '../components/LinkedAccounts';
import PromiseAbortController from './PromiseAbortController';

/**
 * Copy shown in the alert of the {@link AuthModal}. `null` lets the modal pick its own
 * wording depending on whether the account is being linked or re-authenticated.
 */
export type AuthAlertText = Mithril.Children;

/**
 * Field object handed to the Google Analytics `ga('send', …)` command.
 *
 * The named fields are the ones GA itself defines; the index signature covers custom dimensions
 * and metrics.
 */
export interface GoogleAnalyticsSendFields {
	/** The kind of hit being sent — `'event'` for everything this extension tracks. */
	hitType?: string;
	eventCategory?: string;
	eventAction?: string;
	eventLabel?: string;
	eventValue?: number;
	[field: string]: unknown;
}

/**
 * The Google Analytics command queue. It installs itself as a browser global, so it has to
 * be read off `window` rather than imported.
 */
export type GoogleAnalyticsSend = (command: string, fields: GoogleAnalyticsSendFields) => void;

/**
 * A condition polled by {@link Auth.oauthLogin}. Anything truthy resolves the wait; the
 * `void` arm covers conditions that simply fall through without returning.
 */
export type AuthWaitCondition = () => boolean | void;

export default class Auth {
	static async linkAccount(alertText: AuthAlertText = null, gaAttrs: GoogleAnalyticsSendFields | null = null): Promise<void> {
		// To make sure the logic of the linking works properly, we need to first load the linked accounts
		await app.store.find('linked-accounts', {});

		// Then ask the user to link his accounts
		await this.oauthLogin(true, alertText);

		// Track successful OF linking in Google Analytics
		try {
			const gaWindow = window as { ga?: GoogleAnalyticsSend };

			if (typeof gaWindow.ga === 'function' && gaAttrs) {
				gaWindow.ga('send', gaAttrs);
			}
		} catch (e) {
			// Do nothing
		}
	}

	static async oauthLogin(link: boolean = false, alertText: AuthAlertText = null): Promise<void> {
		// console.log('oauthLogin called. Parameters -> link:', link, ', alertText:', alertText);

		const abortController = new PromiseAbortController();

		// console.log('Displaying AuthModal...');
		app.modal.show(AuthModal, { alertText, abortController });

		if (link) {
			// console.log('Link parameter is true. Proceeding with condition checks...');
			// Wait for a condition to be true with a timeout
			const waitForCondition = async (
				conditionFunc: AuthWaitCondition,
				conditionName: string = 'Unnamed Condition',
				timeout: number = 5 * 60 * 1000
			): Promise<boolean> => {
				// console.log(
				// 	`waitForCondition called for "${conditionName}" with timeout of ${timeout}ms`,
				// );
				return new Promise<boolean>((resolve, reject) => {
					const checkInterval = 10;
					const timeoutId = setTimeout(() => {
						clearInterval(intervalId);
						// console.error(`Timeout exceeded for condition "${conditionName}"`);

						reject(new Error(`Timeout exceeded for condition "${conditionName}"`));
					}, timeout);

					const intervalId = setInterval(() => {
						if (conditionFunc()) {
							clearTimeout(timeoutId);
							clearInterval(intervalId);
							// console.log(`Condition "${conditionName}" met, resolving promise`);
							resolve(true);
						} else {
							// console.log(`Condition "${conditionName}" check at interval`);
						}
					}, checkInterval);

					abortController.addRejectCallback(() => {
						// console.error(
						// 	`AbortController reject callback triggered for condition "${conditionName}"`,
						// );

						clearInterval(intervalId); // Clear the interval upon abort
						reject(new Error(`AbortController triggered for condition "${conditionName}"`));
					});
				});
			};

			// Use utility function to wait for conditions
			// console.log('Waiting for oauth_linkingInProgress to become true...');
			await waitForCondition(() => !!app.fof_oauth_linkingInProgress, 'oauth_linkingInProgress becomes true');

			// console.log('Waiting for oauth_linkingInProgress to become false...');
			await waitForCondition((): boolean | void => {
				if (app.fof_oauth_linkingInProgress === false) {
					// console.log('✅ oauth_linkingInProgress is false, resolving promise');
					return true;
				}

				if (typeof app.fof_oauth_linkingInProgress === 'undefined') {
					// console.log('🙈🔴 oauth_linkingInProgress is undefined, resolving promise anyway');
					return true;
				}
			}, 'oauth_linkingInProgress becomes false or is undefined');

			// console.log('Waiting for user attribute linkedShop to become available...');
			await waitForCondition(() => !!app.session.user!.attribute('linkedShop'), 'linkedShop attribute availability');
		}

		// console.log('oauthLogin function completed.');
	}

	static async authenticatedCall<TResult>(
		callback: () => TResult | Promise<TResult>,
		alertText: AuthAlertText = null,
		gaAttrs: GoogleAnalyticsSendFields | null = null
	): Promise<TResult | undefined> {
		if (typeof callback !== 'function') {
			throw new Error('Callback must be a function');
		}

		// If the user is not logged in, ask him to log in
		if (!app.session.user) {
			app.modal.show(LogInModal);
			return;
		}

		if (!app.session.user.linkedShop()) {
			await this.linkAccount(alertText, gaAttrs);
		}

		try {
			return await callback();
		} catch (error) {
			// If the user has an expired token ask to re-authenticate.
			if ((error as RequestError).status === 401) {
				// To make sure the logic of the linking works properly, we need to first load the linked accounts
				await app.store.find('linked-accounts', {});

				// We need to "fake" an "unlinked" account so that the logic doesn't fail
				const fakeUnlinkedAccountData = {
					...app.store.all<LinkedAccountProvider>('linked-accounts').find((a) => a.name() === 'oft')?.data,
				} as SavedModelData;
				fakeUnlinkedAccountData.id = `${app.session.user!.id()}-oft`;
				fakeUnlinkedAccountData.attributes = {
					...fakeUnlinkedAccountData.attributes,
					linked: false,
					identifier: null,
					providerIdentifier: null,
					firstLogin: null,
					lastLogin: null,
				};
				app.store.pushObject(fakeUnlinkedAccountData);

				await this.oauthLogin(true);

				// Retry callback (recursively)
				return await this.authenticatedCall(callback, alertText, gaAttrs);
			} else {
				throw error;
			}
		}
	}
}
